import { AssetState, FolderContent } from '@/api/generatedApi';
import { GET_ASSET_QUERY_KEY } from '@/hooks/api/assets/useGetAsset';
import { LIST_ASSETS_BY_STATE_QUERY_KEY } from '@/hooks/api/assets/useFetchAssetsByState';
import { ASSET_STATE_COUNTS_QUERY_KEY } from '@/hooks/api/assets/useGetAssetStateCounts';
import { LIST_FOLDER_CONTENTS_QUERY_KEY } from '@/hooks/api/folders/useFetchFolderContents';
import { AssetsPageObjectModel } from '@/poms/assetsPageObjectModel';
import { api } from '@/services/api';
import { useTenants } from '@/services/tenants/useTenants';
import { isAsset } from '@/utils/assets';
import { pluralize } from '@/utils/format';
import { Box, Typography } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { BulkModal } from './BulkModal';

export const BulkReprocessModal = ({
  open,
  onClose,
  selectedContent,
  onReprocessComplete,
}: {
  open: boolean;
  onClose: () => void;
  selectedContent: FolderContent[];
  onReprocessComplete?: () => void;
}) => {
  const { getTenantId } = useTenants();
  const queryClient = useQueryClient();

  const assets = selectedContent.filter(content => isAsset(content));
  const folderCount = selectedContent.filter(content => !isAsset(content)).length;
  const eligibleAssets = assets.filter(asset => asset.state !== AssetState.Finished);
  const finishedCount = assets.length - eligibleAssets.length;

  const reprocessAsset = async (asset: FolderContent) => {
    await api.assets.reprocessAsset(getTenantId(), asset.id);
  };

  const handleComplete = () => {
    queryClient.resetQueries({ queryKey: [GET_ASSET_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [LIST_FOLDER_CONTENTS_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [LIST_ASSETS_BY_STATE_QUERY_KEY], exact: false });
    queryClient.resetQueries({ queryKey: [ASSET_STATE_COUNTS_QUERY_KEY], exact: false });
    onReprocessComplete?.();
  };

  const skipCaptions = [
    folderCount > 0 ? `${pluralize(folderCount, 'folder', 'folders')} cannot be reprocessed.` : undefined,
    finishedCount > 0 ? `${pluralize(finishedCount, 'asset', 'assets')} in FINISHED state will be skipped.` : undefined,
  ].filter((line): line is string => line !== undefined);

  return (
    <BulkModal
      open={open}
      onClose={onClose}
      selectedItems={eligibleAssets}
      onComplete={handleComplete}
      performOperation={reprocessAsset}
      isConfirmDisabled={eligibleAssets.length === 0}
      labels={{
        title: 'Reprocess assets',
        action: 'reprocess',
        actionPastTense: 'Queued for reprocessing',
        actionPresent: 'reprocessing',
        confirmButton: 'Reprocess',
        itemSingular: 'asset',
        itemPlural: 'assets',
        description: count => {
          if (count === 0) return 'None of the selected items can be reprocessed.';
          const subject = count === 1 ? 'This asset' : `These ${count} assets`;
          return `${subject} will be queued for reprocessing.`;
        },
      }}
      buttonColor='primary'
      dataTestId={AssetsPageObjectModel.BulkReprocessModal}
      actionsStart={
        skipCaptions.length > 0 ? (
          <Box>
            {skipCaptions.map(line => (
              <Typography key={line} variant='caption' color='text.secondary' display='block'>
                {line}
              </Typography>
            ))}
          </Box>
        ) : undefined
      }
    />
  );
};
